import { invoke } from "@tauri-apps/api/core";
import { atomFamily, atomWithRefresh } from "jotai/utils";

import { Logger } from "@moment/logging";

import { env } from "../env/env";

const logger = new Logger("kubernetes-interop");

export type KubeContext = {
	name: string;
	cluster: string;
	namespace: string | null;
};

export type KubeHttpRequest = {
	context: string;
	kubeconfig_path: string | null;
	method: string;
	path: string;
	headers: Record<string, string> | null;
	body: string | null;
};

export type KubeHttpResponse = {
	status: number;
	status_text: string;
	headers: Record<string, string>;
	body: string;
	body_base64: string | null;
	duration_ms: number;
};

export type KubernetesAPI = {
	listContexts: (kubeconfigPath?: string) => Promise<KubeContext[]>;
	executeRequest: (request: KubeHttpRequest) => Promise<KubeHttpResponse>;
};

const tauriKubernetesAPI = (): KubernetesAPI => ({
	listContexts: async (kubeconfigPath?: string) => {
		const path = kubeconfigPath?.trim() || null;
		logger.debug("listContexts called");
		return invoke<KubeContext[]>("list_kube_contexts", {
			kubeconfigPath: path,
		});
	},
	executeRequest: async (request: KubeHttpRequest) => {
		logger.debug("executeRequest called", { context: request.context, path: request.path });
		return invoke<KubeHttpResponse>("execute_kube_request", { request });
	},
});

const noKubernetesAPI: KubernetesAPI = {
	listContexts: async () => {
		logger.warn("Kubernetes API not available outside desktop app");
		return [];
	},
	executeRequest: async () => {
		throw new Error("Kubernetes API not available outside desktop app");
	},
};

export const kubernetes = (): KubernetesAPI => {
	if (env.isDesktopApp) {
		return tauriKubernetesAPI();
	}
	return noKubernetesAPI;
};

export const $kubeContexts = atomFamily((kubeconfigPath: string | undefined) => {
	const contextsAtom = atomWithRefresh(async () => {
		const api = kubernetes();
		return api.listContexts(kubeconfigPath);
	});
	contextsAtom.debugLabel = `kubeContexts/${kubeconfigPath ?? "default"}`;
	return contextsAtom;
});
